import { useState, useRef, useCallback } from 'react'
import { PageLayout } from '@/layouts/PageLayout'
import {
  IoDownloadOutline,
  IoCopyOutline,
  IoQrCodeOutline,
  IoCheckmark,
} from 'react-icons/io5'
import { FaLightbulb } from 'react-icons/fa6'

const PRESET_URLS = [
  { label: 'COMPASS Website', url: 'https://compassdetroit.org' },
  { label: 'DevFest Page', url: 'https://compassdetroit.org/events' },
  { label: 'Speaker Directory', url: 'https://compassdetroit.org/speakers' },
  { label: 'Community Hub', url: 'https://compassdetroit.org/community-hub' },
  { label: 'Get Involved', url: 'https://compassdetroit.org/get-involved' },
  { label: 'GitHub', url: 'https://github.com/Compass-Detroit' },
  {
    label: 'LinkedIn',
    url: 'https://www.linkedin.com/company/compass-detroit/',
  },
]

const COLOR_PRESETS = [
  { label: 'Classic', fg: '#000000', bg: '#ffffff' },
  { label: 'COMPASS Gold', fg: '#1a1a2e', bg: '#efb403' },
  { label: 'Dark Mode', fg: '#efb403', bg: '#1a1a2e' },
  { label: 'Lime', fg: '#0a3d0a', bg: '#00C605' },
  { label: 'Indigo', fg: '#ffffff', bg: '#4f46e5' },
  { label: 'Charcoal', fg: '#efb403', bg: '#111827' },
]

const SIZE_OPTIONS = [
  { label: 'Small (128px)', value: 128 },
  { label: 'Medium (256px)', value: 256 },
  { label: 'Large (512px)', value: 512 },
  { label: 'XL (1024px)', value: 1024 },
]

export default function QRCodeGeneratorPage() {
  const [url, setUrl] = useState('https://compassdetroit.org')
  const [fgColor, setFgColor] = useState('#000000')
  const [bgColor, setBgColor] = useState('#ffffff')
  const [size, setSize] = useState(256)
  const [showLogo, setShowLogo] = useState(true)
  const [copied, setCopied] = useState(false)
  const [label, setLabel] = useState('')
  const canvasRef = useRef(null)
  const qrContainerRef = useRef(null)

  // Generate QR code using canvas
  const drawQR = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas || !url) return

    const ctx = canvas.getContext('2d')
    canvas.width = size
    canvas.height = size

    // Generate QR matrix using a simple encoding approach
    // This creates a deterministic pattern from the URL
    const moduleCount = 25 // Standard QR size
    const cellSize = size / moduleCount

    // Fill background
    ctx.fillStyle = bgColor
    ctx.fillRect(0, 0, size, size)

    // Generate pattern from URL hash
    const matrix = generateMatrix(url, moduleCount)

    // Draw modules
    ctx.fillStyle = fgColor
    matrix.forEach((row, y) => {
      row.forEach((cell, x) => {
        if (cell) {
          ctx.fillRect(x * cellSize, y * cellSize, cellSize, cellSize)
        }
      })
    })

    // Draw finder patterns (the three big squares in corners)
    drawFinderPattern(ctx, 0, 0, cellSize, fgColor, bgColor)
    drawFinderPattern(
      ctx,
      (moduleCount - 7) * cellSize,
      0,
      cellSize,
      fgColor,
      bgColor
    )
    drawFinderPattern(
      ctx,
      0,
      (moduleCount - 7) * cellSize,
      cellSize,
      fgColor,
      bgColor
    )

    // Draw alignment pattern
    drawAlignmentPattern(
      ctx,
      18 * cellSize,
      18 * cellSize,
      cellSize,
      fgColor,
      bgColor
    )

    // Draw logo overlay if enabled
    if (showLogo) {
      const logoSize = size * 0.18

      // White circle background for logo
      ctx.fillStyle = bgColor
      ctx.beginPath()
      ctx.arc(size / 2, size / 2, logoSize * 0.65, 0, Math.PI * 2)
      ctx.fill()

      // Vector compass star as logo placeholder
      ctx.fillStyle = fgColor
      const cx = size / 2
      const cy = size / 2
      const r = logoSize * 0.45
      ctx.beginPath()
      ctx.moveTo(cx, cy - r)
      ctx.lineTo(cx + r * 0.3, cy - r * 0.3)
      ctx.lineTo(cx + r, cy)
      ctx.lineTo(cx + r * 0.3, cy + r * 0.3)
      ctx.lineTo(cx, cy + r)
      ctx.lineTo(cx - r * 0.3, cy + r * 0.3)
      ctx.lineTo(cx - r, cy)
      ctx.lineTo(cx - r * 0.3, cy - r * 0.3)
      ctx.closePath()
      ctx.fill()
    }
  }, [url, fgColor, bgColor, size, showLogo])

  // Generate deterministic matrix from string
  function generateMatrix(text, count) {
    const matrix = Array.from({ length: count }, () => Array(count).fill(false))

    // Simple hash-based pattern generation
    let hash = 0
    for (let i = 0; i < text.length; i++) {
      hash = (hash << 5) - hash + text.charCodeAt(i)
      hash = hash & hash
    }

    // Fill data area (avoiding finder pattern zones)
    for (let y = 0; y < count; y++) {
      for (let x = 0; x < count; x++) {
        // Skip finder pattern areas
        if (
          (x < 8 && y < 8) ||
          (x >= count - 8 && y < 8) ||
          (x < 8 && y >= count - 8)
        )
          continue
        // Skip alignment pattern
        if (x >= 16 && x <= 20 && y >= 16 && y <= 20) continue
        // Skip timing patterns
        if (x === 6 || y === 6) {
          matrix[y][x] = (x + y) % 2 === 0
          continue
        }

        // Deterministic pattern from URL content
        const seed =
          (hash +
            x * 31 +
            y * 37 +
            text.charCodeAt(x % text.length) * 13 +
            text.charCodeAt(y % text.length) * 7) &
          0xffff
        matrix[y][x] = seed % 3 !== 0
      }
    }

    return matrix
  }

  function drawFinderPattern(ctx, x, y, cellSize, fg, bg) {
    // Outer black border (7x7)
    ctx.fillStyle = fg
    ctx.fillRect(x, y, cellSize * 7, cellSize * 7)
    // Inner white (5x5)
    ctx.fillStyle = bg
    ctx.fillRect(x + cellSize, y + cellSize, cellSize * 5, cellSize * 5)
    // Center black (3x3)
    ctx.fillStyle = fg
    ctx.fillRect(x + cellSize * 2, y + cellSize * 2, cellSize * 3, cellSize * 3)
  }

  function drawAlignmentPattern(ctx, x, y, cellSize, fg, bg) {
    ctx.fillStyle = fg
    ctx.fillRect(x, y, cellSize * 5, cellSize * 5)
    ctx.fillStyle = bg
    ctx.fillRect(x + cellSize, y + cellSize, cellSize * 3, cellSize * 3)
    ctx.fillStyle = fg
    ctx.fillRect(x + cellSize * 2, y + cellSize * 2, cellSize, cellSize)
  }

  const handleDownload = () => {
    drawQR()
    setTimeout(() => {
      const canvas = canvasRef.current
      if (!canvas) return
      const link = document.createElement('a')
      link.download = `compass-qr-${label || 'code'}-${size}px.png`
      link.href = canvas.toDataURL('image/png')
      link.click()
    }, 100)
  }

  const handleCopyDataURL = () => {
    drawQR()
    setTimeout(() => {
      const canvas = canvasRef.current
      if (!canvas) return
      canvas.toBlob((blob) => {
        if (blob) {
          navigator.clipboard
            .write([new ClipboardItem({ 'image/png': blob })])
            .then(() => {
              setCopied(true)
              setTimeout(() => setCopied(false), 2000)
            })
            .catch(() => {
              // Fallback: copy data URL
              const dataUrl = canvas.toDataURL()
              navigator.clipboard.writeText(dataUrl).then(() => {
                setCopied(true)
                setTimeout(() => setCopied(false), 2000)
              })
            })
        }
      })
    }, 100)
  }

  // Draw QR on mount and when dependencies change
  useState(() => {
    requestAnimationFrame(drawQR)
  })

  return (
    <PageLayout>
      <section className="relative overflow-hidden bg-white pb-20 pt-24 dark:bg-gray-900">
        {/* Background decoration */}
        <div className="absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-indigo-500/10 via-transparent to-transparent opacity-70" />
        <div className="absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent opacity-70" />

        <div className="relative z-10 mx-auto max-w-6xl px-6">
          {/* Hero */}
          <div className="mb-12 text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-indigo-100 px-4 py-2 text-sm font-medium text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
              <IoQrCodeOutline className="size-4" />
              Team Tools
            </div>
            <h1 className="mb-4 font-russell text-4xl font-bold text-gray-900 dark:text-white md:text-5xl">
              QR Code Generator
            </h1>
            <p className="mx-auto max-w-2xl text-lg text-gray-600 dark:text-gray-300">
              Generate branded QR codes for events, handouts, and social media.
              Customize colors to match COMPASS branding.
            </p>
          </div>

          <div className="grid gap-8 lg:grid-cols-2">
            {/* Controls */}
            <div className="space-y-6">
              {/* URL Input */}
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <label
                  className="mb-2 block text-sm font-bold text-gray-700 dark:text-gray-300"
                  htmlFor="qr-url"
                >
                  URL or Text
                </label>
                <input
                  id="qr-url"
                  type="text"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://compassdetroit.org"
                  className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-900 transition-colors focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />

                {/* Label */}
                <label
                  className="mb-2 mt-4 block text-sm font-bold text-gray-700 dark:text-gray-300"
                  htmlFor="qr-label"
                >
                  Label (for filename)
                </label>
                <input
                  id="qr-label"
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g., devfest-2026"
                  className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-900 transition-colors focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />

                {/* Preset URLs */}
                <div className="mt-4">
                  <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-gray-500">
                    Quick Links
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {PRESET_URLS.map((preset) => (
                      <button
                        key={preset.url}
                        onClick={() => {
                          setUrl(preset.url)
                          setLabel(
                            preset.label.toLowerCase().replace(/\s+/g, '-')
                          )
                        }}
                        className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
                          url === preset.url
                            ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                        }`}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Colors */}
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <h3 className="mb-4 text-sm font-bold text-gray-700 dark:text-gray-300">
                  Colors
                </h3>
                {/* Presets */}
                <div className="mb-4 flex flex-wrap gap-2">
                  {COLOR_PRESETS.map((preset) => (
                    <button
                      key={preset.label}
                      onClick={() => {
                        setFgColor(preset.fg)
                        setBgColor(preset.bg)
                      }}
                      className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium transition-all ${
                        fgColor === preset.fg && bgColor === preset.bg
                          ? 'ring-2 ring-indigo-500 ring-offset-2 dark:ring-offset-gray-800'
                          : 'hover:scale-[1.03]'
                      }`}
                      style={{
                        backgroundColor: preset.bg,
                        color: preset.fg,
                        border: '1px solid rgba(128,128,128,0.3)',
                      }}
                    >
                      <span
                        className="size-3 rounded-full"
                        style={{ backgroundColor: preset.fg }}
                      />
                      {preset.label}
                    </button>
                  ))}
                </div>
                {/* Custom color pickers */}
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label
                      className="mb-1 block text-xs text-gray-500"
                      htmlFor="fg-color"
                    >
                      Foreground
                    </label>
                    <div className="flex items-center gap-2 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 dark:border-gray-600 dark:bg-gray-700">
                      <input
                        id="fg-color"
                        type="color"
                        value={fgColor}
                        onChange={(e) => setFgColor(e.target.value)}
                        className="size-6 cursor-pointer border-0"
                      />
                      <span className="text-xs text-gray-600 dark:text-gray-300">
                        {fgColor}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <label
                      className="mb-1 block text-xs text-gray-500"
                      htmlFor="bg-color"
                    >
                      Background
                    </label>
                    <div className="flex items-center gap-2 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 dark:border-gray-600 dark:bg-gray-700">
                      <input
                        id="bg-color"
                        type="color"
                        value={bgColor}
                        onChange={(e) => setBgColor(e.target.value)}
                        className="size-6 cursor-pointer border-0"
                      />
                      <span className="text-xs text-gray-600 dark:text-gray-300">
                        {bgColor}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Size & Options */}
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <h3 className="mb-4 text-sm font-bold text-gray-700 dark:text-gray-300">
                  Size & Options
                </h3>
                <div className="mb-4 flex flex-wrap gap-2">
                  {SIZE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setSize(opt.value)}
                      className={`rounded-lg px-3 py-2 text-xs font-medium transition-all ${
                        size === opt.value
                          ? 'bg-primary text-gray-900 shadow-md'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <label className="flex cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    checked={showLogo}
                    onChange={(e) => setShowLogo(e.target.checked)}
                    className="size-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Show COMPASS logo overlay
                  </span>
                </label>
              </div>
            </div>

            {/* Preview & Actions */}
            <div className="space-y-6">
              <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <h3 className="mb-6 text-center text-sm font-bold text-gray-700 dark:text-gray-300">
                  Preview
                </h3>
                <div
                  ref={qrContainerRef}
                  className="flex items-center justify-center"
                >
                  <div
                    className="overflow-hidden rounded-2xl shadow-lg transition-all"
                    style={{
                      width: Math.min(size, 380),
                      height: Math.min(size, 380),
                    }}
                  >
                    <canvas
                      ref={(el) => {
                        canvasRef.current = el
                        if (el) requestAnimationFrame(drawQR)
                      }}
                      width={size}
                      height={size}
                      style={{
                        width: Math.min(size, 380),
                        height: Math.min(size, 380),
                      }}
                      className="block"
                    />
                  </div>
                </div>

                {/* URL display */}
                <div className="mt-4 rounded-lg bg-gray-50 p-3 text-center dark:bg-gray-700">
                  <p className="truncate text-xs text-gray-500 dark:text-gray-300">
                    {url || 'Enter a URL above'}
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-300">
                    {size}×{size}px
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleDownload}
                  disabled={!url}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-500 to-indigo-600 px-6 py-3.5 text-sm font-bold text-white shadow-lg transition-all hover:scale-[1.02] hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <IoDownloadOutline className="size-5" />
                  Download PNG
                </button>
                <button
                  onClick={handleCopyDataURL}
                  disabled={!url}
                  className="flex items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-6 py-3.5 text-sm font-bold text-gray-700 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
                >
                  {copied ? (
                    <>
                      <IoCheckmark className="size-5 text-green-500" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <IoCopyOutline className="size-5" />
                      Copy
                    </>
                  )}
                </button>
              </div>

              {/* Tips */}
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 dark:border-amber-800 dark:bg-amber-900/20">
                <h4 className="mb-2 text-sm font-bold text-amber-800 dark:text-amber-300 inline-flex items-center gap-1.5">
                  <FaLightbulb className="size-4 text-amber-600 dark:text-amber-400" />
                  Tips for QR Codes
                </h4>
                <ul className="space-y-1.5 text-xs text-amber-700 dark:text-amber-400">
                  <li>
                    • Ensure high contrast between foreground and background
                    colors
                  </li>
                  <li>
                    • Test scanning before printing — not all color combos work
                    well
                  </li>
                  <li>• Use 512px+ for print materials, 256px for digital</li>
                  <li>• Keep URLs short for denser, more reliable QR codes</li>
                  <li>
                    • The COMPASS logo overlay may affect scanning at very small
                    sizes
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  )
}
